import { useState, type ReactNode } from 'react';
import { Box, Button, Card, CardContent, Collapse, Divider, Link, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PriceLabel } from './PriceLabel';
import { OutOfBudgetText } from './OutOfBudgetChip';

export interface ResultCardProps {
  /** Sol taraftaki görsel/ikon (havayolu logosu, araç ikonu, otel foto). */
  media?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Çip satırı — süre, aktarma, olanaklar, sağlayıcı vs. */
  attributes?: ReactNode;
  price: { amount: number | null | undefined; currency?: string | null; note?: ReactNode; wasAmount?: number };
  /** Dolu ise fiyatın altına "Kural Dışı Fiyat (bütçe: …)" + buton "Onaya Gönder". */
  outOfBudget?: { maxBudget?: number | null };
  primaryAction: { label?: string; onClick: () => void; disabled?: boolean };
  secondaryActions?: ReactNode;
  /** Genişletilebilir detay (segmentler, oda listesi). */
  expandable?: ReactNode;
  expandLabel?: string;
  selected?: boolean;
  dense?: boolean;
}

/** Tüm ürün sonuç kartlarının ortak yapısı — bugün her ürün kendi kartını icat ediyor. */
export function ResultCard({
  media,
  title,
  subtitle,
  attributes,
  price,
  outOfBudget,
  primaryAction,
  secondaryActions,
  expandable,
  expandLabel = 'Detayları göster',
  selected = false,
  dense = false,
}: ResultCardProps) {
  const [open, setOpen] = useState(false);
  const oob = !!outOfBudget;
  const actionLabel = primaryAction.label ?? (oob ? 'Onaya Gönder' : 'Seç');

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
      }}
    >
      <CardContent sx={{ p: dense ? 1.5 : 2, '&:last-child': { pb: dense ? 1.5 : 2 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}
        >
          <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, flexGrow: 1 }}>
            {media && <Box sx={{ flexShrink: 0 }}>{media}</Box>}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
              {attributes && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}>
                  {attributes}
                </Stack>
              )}
            </Box>
          </Stack>

          <Box sx={{ textAlign: { sm: 'right' }, flexShrink: 0 }}>
            <PriceLabel
              amount={price.amount}
              currency={price.currency}
              size="lg"
              emphasis
              align="right"
              secondary={price.note as string | undefined}
            />
            {price.wasAmount != null && (
              <PriceLabel amount={price.wasAmount} currency={price.currency} size="sm" strikethrough align="right" />
            )}
            {oob && <OutOfBudgetText maxBudget={outOfBudget?.maxBudget} />}
            <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: { sm: 'flex-end' } }}>
              {secondaryActions}
              <Button
                variant={selected ? 'outlined' : 'contained'}
                color={oob ? 'warning' : 'primary'}
                disabled={primaryAction.disabled}
                onClick={primaryAction.onClick}
              >
                {selected ? 'Seçildi' : actionLabel}
              </Button>
            </Stack>
          </Box>
        </Stack>

        {expandable && (
          <>
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => setOpen((v) => !v)}
              sx={{ mt: 1, display: 'inline-flex', alignItems: 'center' }}
            >
              {open ? 'Detayları gizle' : expandLabel}
              <ExpandMoreIcon fontSize="small" sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />
            </Link>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 1.5 }} />
              {expandable}
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
}
